import { Link, useSearchParams } from 'react-router-dom';
import type { QueueFilter, ReviewQueueItem } from '@/types/api';
import { CHAT_STATUS } from '@/lib/copy';
import { formatAge, formatDateTime, pluralize } from '@/lib/format';
import { useDocumentTitle, useNow } from '@/lib/hooks';
import { cn } from '@/lib/utils';
import { PageBody, PageHeader } from '@/components/layout/page';
import { Flag, StateChip } from '@/components/state-chip';
import { Empty } from '@/components/ui/empty';
import { Pagination } from '@/components/ui/pagination';
import { Panel } from '@/components/ui/panel';
import { TableSkeleton } from '@/components/ui/skeleton';
import { Table, TBody, TD, TDMono, TH, THead, TR } from '@/components/ui/table';
import { useQueue } from './queries';
import { ClaimControl, ClaimState } from './claim-control';

/* ---------------------------------------------------------------------------
   The review queue.

   Every row here is a frozen project and a client who cannot prompt. That is
   what the queue sorts on and what the age column is for: a chat that has been
   in `pending review` for six hours is not a backlog item, it is a customer
   locked out of their own product.

   The column layout encodes the one thing about this screen that is easy to get
   wrong. STATUS and CHECKED OUT are separate columns, because check-out is not
   a status: every row here reads `pending review` (or `promotion failed`), and
   the assignment column plus its lease is the only thing that distinguishes a
   queued chat from one somebody is already working.
--------------------------------------------------------------------------- */

const FILTERS: { value: QueueFilter; label: string; hint: string }[] = [
  { value: 'all', label: 'All', hint: 'Everything in review across your projects.' },
  { value: 'unclaimed', label: 'Unclaimed', hint: 'Nobody holds these. Each one is a frozen project.' },
  { value: 'mine', label: 'Yours', hint: 'Chats you hold. Your lease refreshes while you work.' },
  { value: 'promotion_failed', label: 'Needs an operator', hint: 'A sequential merge died partway. No automated recovery exists.' },
];

function isFilter(value: string | null): value is QueueFilter {
  return value === 'all' || value === 'unclaimed' || value === 'mine' || value === 'promotion_failed';
}

export function QueuePage() {
  useDocumentTitle('Review queue');
  const [params, setParams] = useSearchParams();

  const filterParam = params.get('filter');
  const filter: QueueFilter = isFilter(filterParam) ? filterParam : 'all';
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);

  const queue = useQueue(filter, page);
  const rows = queue.data?.items ?? [];
  const now = useNow(true, 30_000);

  const setQuery = (next: { filter?: QueueFilter; page?: number }) => {
    const merged = new URLSearchParams(params);
    if (next.filter) {
      merged.set('filter', next.filter);
      merged.delete('page');
    }
    if (next.page) merged.set('page', String(next.page));
    setParams(merged, { replace: true });
  };

  const active = FILTERS.find((f) => f.value === filter) ?? FILTERS[0]!;

  return (
    <>
      <PageHeader
        title="Review queue"
      />

      <PageBody>
        <Panel>
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-rule px-3 py-2">
            <div role="tablist" aria-label="Queue filter" className="flex flex-wrap items-center gap-0.5">
              {FILTERS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="tab"
                  aria-selected={filter === option.value}
                  title={option.hint}
                  onClick={() => setQuery({ filter: option.value })}
                  className={cn(
                    'rounded-sm px-2 py-1 font-mono text-tiny uppercase tracking-[0.08em] transition-colors',
                    filter === option.value
                      ? 'bg-sunken text-ink'
                      : 'text-ink-3 hover:bg-sunken hover:text-ink-2',
                    option.value === 'promotion_failed' && filter !== option.value && 'text-danger/80',
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-ink-3">{active.hint}</p>
          </div>

          {queue.isLoading ? (
            <TableSkeleton rows={5} cols={6} />
          ) : queue.isError ? (
            <Empty
              title="The queue did not load"
              body={queue.error.message}
              action={
                <button
                  type="button"
                  onClick={() => void queue.refetch()}
                  className="font-mono text-tiny uppercase tracking-[0.08em] text-action underline underline-offset-2"
                >
                  Retry
                </button>
              }
            />
          ) : rows.length === 0 ? (
            <EmptyQueue filter={filter} />
          ) : (
            <Table>
              <THead>
                <TR className="hover:bg-transparent">
                  <TH>Project</TH>
                  <TH>Waiting</TH>
                  <TH>Status</TH>
                  <TH title="Check-out is not a status. It is the assignment column plus a lease.">Checked out</TH>
                  <TH>Change</TH>
                  <TH className="text-right">Action</TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((item) => (
                  <QueueRow key={item.chat_id} item={item} now={now} />
                ))}
              </TBody>
            </Table>
          )}

          <Pagination meta={queue.data?.meta} unit="chat" onPage={(next) => setQuery({ page: next })} />
        </Panel>
      </PageBody>
    </>
  );
}

function QueueRow({ item, now }: { item: ReviewQueueItem; now: number }) {
  const status = CHAT_STATUS[item.status];
  const needsOperator = item.needs_operator === true || item.status === 'promotion failed';
  const claimable = item.status === 'pending review';
  const age = formatAge(item.waiting_since, now);
  const stale = now - new Date(item.waiting_since).getTime() > 4 * 3600_000;

  return (
    <TR className={cn(needsOperator && 'bg-danger-soft/50')}>
      <TD className="max-w-[22rem]">
        <Link to={`/chats/${item.chat_id}`} className="block min-w-0 hover:text-action">
          <span className="block truncate font-medium text-ink">{item.project_name}</span>
          <span className="mt-0.5 flex flex-wrap items-center gap-x-2 font-mono text-tiny text-ink-3">
            <span>chat {item.chat_id}</span>
            {item.client_display_name ? <span className="truncate">· {item.client_display_name}</span> : null}
          </span>
        </Link>
      </TD>

      <TDMono>
        <span
          className={cn('tabular-nums', stale ? 'text-warn' : 'text-ink-2')}
          title={`In review since ${formatDateTime(item.waiting_since)}`}
        >
          {age}
        </span>
      </TDMono>

      <TD>
        <div className="flex flex-wrap items-center gap-1.5">
          <StateChip state={status} size="sm" />
          {needsOperator ? <Flag label="operator" tone="danger" title="promotion failed — repair happens outside the platform" /> : null}
          {item.context_exhausted ? (
            <Flag label="1M" tone="warn" title="Context ceiling reached. The composer is locked for both roles; promotion stays available." />
          ) : null}
        </div>
      </TD>

      <TD>
        <ClaimState claim={item.claim ?? null} />
      </TD>

      <TD className="max-w-[24rem]">
        <span className="block font-mono text-tiny text-ink-3">
          {item.changed_repo_count ?? 0} of {pluralize(item.repo_count ?? 0, 'repo')} touched
        </span>
        {item.last_client_message_excerpt ? (
          <span className="mt-0.5 block truncate text-xs text-ink-2" title={item.last_client_message_excerpt}>
            “{item.last_client_message_excerpt}”
          </span>
        ) : null}
      </TD>

      <TD className="text-right">
        <div className="flex justify-end">
          <ClaimControl chatId={item.chat_id} claim={item.claim ?? null} claimable={claimable} />
        </div>
      </TD>
    </TR>
  );
}

function EmptyQueue({ filter }: { filter: QueueFilter }) {
  if (filter === 'promotion_failed') {
    return (
      <Empty
        title="No failed promotions"
        body="Nothing on your projects died mid-merge. This is the filter to check first when an operator pages you."
      />
    );
  }
  if (filter === 'mine') {
    return (
      <Empty
        title="You hold nothing right now"
        body="Claim a chat from the unclaimed filter to start a review. Claiming takes the assignment; it does not move the chat’s status."
      />
    );
  }
  if (filter === 'unclaimed') {
    return <Empty title="Nothing is waiting" body="Every chat in review is checked out. No project is frozen without an owner." />;
  }
  return (
    <Empty
      title="Nothing is in review"
      body="Chats arrive here when a client presses Done. Until then their projects are unfrozen and the thread is theirs."
    />
  );
}
